import type { KeyboardEvent } from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { LOGIN } from '@hotjar/webapp/constants/routes/auth';
import { Text, PasswordInput, Button, tokens, Alert, Link } from '@hotjar/hotsauce';
import { LoginLayout } from '@hotjar/webapp/features/Login';
import { Form, Title, StyledField } from '@hotjar/webapp/features/Login/styles';
import { PasswordInputWithValidation } from '@hotjar/webapp/components/PasswordInputWithValidation';
import { ErrorText } from '@hotjar/webapp/components/ErrorText';
import { resetPassword } from '@hotjar/webapp/features/Password/api';
import { hjGetCookie } from '@hotjar/webapp/utils/cookies';
import { restDefaultHandlers } from '@hotjar/webapp/utils/rest-default-handlers';

import { Subheader, SendEmailButton as SubmitButton, Content } from './forgot-password-styles';

type PageVersion = 'reset-password' | 'set-password';

const SuccessResponse = () => {
  const { t } = useTranslation();

  return (
    <div data-testid="reset-success-message">
      <Alert
        css={{ 'margin-bottom': tokens.spacer.x5 }}
        title={t(`password.success-alert`)}
        kind="success"
      >
        <div />
      </Alert>
      <Subheader>
        <Trans i18nKey="password.success-text">
          <Link href="/login" kind="inline">
            sign in
          </Link>
        </Trans>
      </Subheader>
    </div>
  );
};

interface ErrorResponseProps {
  version: PageVersion;
  errorReason: string;
}
const ErrorResponse = ({ version = 'reset-password', errorReason }: ErrorResponseProps) => {
  const { t } = useTranslation();

  return (
    <div data-testid="reset-error-message">
      <Alert
        css={{ 'margin-bottom': tokens.spacer.x5 }}
        title={t(`password.${version}.submit-error-alert`)}
        kind="danger"
      >
        <div />
      </Alert>
      {errorReason && (
        <>
          <Text textType="subtitle2">{errorReason}</Text>
          <br />
        </>
      )}
      <Subheader>
        <Trans i18nKey={`password.${version}.submit-error-text-1`}>
          <Link href="/password/forgot" kind="inline">
            Forgot Password
          </Link>
        </Trans>
        <br />
        <br />
        <Trans i18nKey={`password.${version}.submit-error-text-2`}>
          <Link href="https://www.hotjar.com/contact" kind="inline" isExternal>
            Contact us
          </Link>
        </Trans>
      </Subheader>
    </div>
  );
};

const getToken = (): string | null => {
  return hjGetCookie('passwd_reset_token');
};

export const ResetPasswordPage = ({ version }: { version: PageVersion }): JSX.Element => {
  const [password, setPassword] = useState<string>('');
  // isSubmitInProgress is used to show submit loading state
  const [isSubmitInProgress, setSubmitInProgress] = useState(false);
  // isSubmitAttempted is used to show field validation errors if validation didn't pass
  const [isSubmitAttempted, setIsSubmitAttempted] = useState<boolean>(false);
  const [confirm, setConfirm] = useState<string>('');
  const [noMatch, setNoMatch] = useState<boolean>(false);
  const [isPasswordValid, setIsPasswordValid] = useState<boolean>(false);
  const [responseType, setResponseType] = useState('');
  const [errorReason, setErrorReason] = useState('');
  const { t } = useTranslation();
  const navigate = useNavigate();
  const track = useTrack();
  const token = getToken();

  let submitResultScreen = null;

  const handleFormSubmit = () => {
    setIsSubmitAttempted(true);

    if (confirm !== password) {
      setNoMatch(true);
      return;
    }

    setSubmitInProgress(true);

    resetPassword({ password, confirm, token }).then(
      (response) => {
        if (response?.success) {
          setResponseType('success');
          track.formSubmit('Reset password', true, {}, {});
        } else {
          const error = response?.error?.token?.[0] || t('password.reset-password.default-error');

          setErrorReason(error as string);
          setResponseType('error');
          track.formSubmit('Reset password', false, {}, { error });
        }
      },
      (res) => {
        setResponseType('error');
        track.formSubmit('Reset password', false, {}, {});
        restDefaultHandlers.onError(res);
      },
    );
  };

  const handlePressEnter = (event: KeyboardEvent) => {
    if (event.key !== 'Enter') return;

    setIsSubmitAttempted(true);

    if (isPasswordValid) {
      handleFormSubmit();
    }
  };

  if (responseType === 'success') {
    submitResultScreen = <SuccessResponse />;
  } else if (responseType === 'error') {
    submitResultScreen = <ErrorResponse version={version} errorReason={errorReason} />;
  } else if (!token) {
    submitResultScreen = (
      <ErrorResponse
        version={version}
        errorReason="Something went wrong while reading the token. Please try again!"
      />
    );
  }

  const content = (
    <Content>
      {submitResultScreen || (
        <>
          <PasswordInputWithValidation
            placeholder={`password.${version}.input-placeholder`}
            required={isSubmitAttempted}
            isPasswordValidCallback={(isValid) => setIsPasswordValid(!!isValid)}
            onChange={(val) => {
              setPassword(val);
              setNoMatch(false);
            }}
            onKeyPress={handlePressEnter}
          />
          <StyledField size="l" labelText={t(`password.confirm`)} inputId="confirm-password">
            <PasswordInput
              id="new_password_confirm"
              name="new_password_confirm"
              data-testid="confirm-password"
              placeholder={t('password.password')}
              size="l"
              onChange={(e) => {
                setConfirm(e.target.value);
                setNoMatch(false);
              }}
              onKeyPress={handlePressEnter}
              isInvalid={noMatch || (isSubmitAttempted && !confirm)}
            />
            {noMatch && (
              <ErrorText data-testid="no-match-error-message">
                {t('password.reset-password.no-match-error')}
              </ErrorText>
            )}
          </StyledField>
          <SubmitButton
            id="submit"
            data-testid="reset-password-btn"
            isLoading={isSubmitInProgress}
            isDisabled={!password || !confirm || isSubmitInProgress || !isPasswordValid}
            onClick={handleFormSubmit}
            size="l"
          >
            {t(`password.${version}.cta-btn`)}
          </SubmitButton>
        </>
      )}
    </Content>
  );

  return (
    <LoginLayout>
      <Form
        id="reset-password-form"
        onSubmit={(e) => {
          e.preventDefault();
        }}
      >
        <Title>{t(`password.${version}.heading`)}</Title>
        <Subheader>{t(`password.${version}.sub-heading`)}</Subheader>
        {content}
        {version === 'reset-password' && (
          <Button
            icon="arrow_left"
            kind="ghost"
            data-testid="go-login-btn"
            onClick={() => navigate(LOGIN.path)}
          >
            {t('password.forgot-password.go-login')}
          </Button>
        )}
      </Form>
    </LoginLayout>
  );
};
